import { UseFormReturn, useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { previewManagedEventTypes } from "@/features/supply-config/managed-events-api";
import { getTeams } from "@/features/supply-config/teams-api";
import { ManagedEventWizardData } from "./wizard-form";

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-4 py-2 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

export default function StepReview({
  form,
}: {
  form: UseFormReturn<ManagedEventWizardData>;
}) {
  const v = useWatch({ control: form.control });
  const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams });
  const teamIds = v.team_ids ?? [];
  const teamNames = teams
    .filter((t) => teamIds.includes(t.id))
    .map((t) => t.name);

  const { data: preview } = useQuery({
    queryKey: ["preview-event-types", teamIds, v.name],
    queryFn: () =>
      previewManagedEventTypes({ team_ids: teamIds, name: v.name! }),
    enabled: teamIds.length > 0 && !!v.name,
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
        <div className="px-4 py-3 bg-gray-50">
          <p className="text-base font-semibold text-gray-900">{v.name}</p>
          <p className="text-xs text-gray-500">
            {v.category} · {v.status}
          </p>
        </div>
        <Row label="Teams" value={teamNames.join(", ") || "—"} />
        <Row label="Attendees" value={v.participant_scope} />
        <Row
          label="Duration"
          value={v.duration_minutes ? `${v.duration_minutes}m` : "—"}
        />
        <Row
          label="Round-robin pool"
          value={v.round_robin_pool_active ? "Active (configure later)" : "Off"}
        />
      </div>

      <div className="rounded-md border border-gray-200 p-3 text-sm space-y-1">
        <p>
          Will create individual event types for{" "}
          <strong className="text-green-700">
            {preview?.matched.length ?? "…"}
          </strong>
          {preview ? ` of ${preview.totalProfessionals}` : ""} professionals
          (across {teamIds.length} team{teamIds.length === 1 ? "" : "s"}).
        </p>
        {preview && preview.unmatched.length > 0 && (
          <p className="text-amber-600 text-xs">
            {preview.unmatched.length} professional(s) won't be matched.
          </p>
        )}
        {v.round_robin_pool_active && (
          <p className="text-xs text-gray-400">
            Round-robin events are added afterwards from Configure.
          </p>
        )}
      </div>
    </div>
  );
}
